import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/ui/Sidebar';
import Icon from '../components/AppIcon';
import Select from '../components/ui/Select';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import adminService from '../../../services/adminService';
import { toArabicDigits } from '../../../utils/numbers';

const TIME_RANGE_OPTIONS = [
  { value: 'week', label: 'آخر أسبوع' },
  { value: 'month', label: 'آخر شهر' },
  { value: '3months', label: 'آخر ٣ أشهر' },
  { value: '6months', label: 'آخر ٦ أشهر' },
  { value: 'year', label: 'آخر سنة' },
];

const SettingsAnalytics = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [circleDistribution, setCircleDistribution] = useState([]);
  const [monthlyEngagement, setMonthlyEngagement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('6months');

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminService.getAnalytics({ timeRange });
      setSummary(data.summary || null);
      setCircleDistribution(data.circleDistribution || []);
      setMonthlyEngagement(data.monthlyEngagement || []);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('فشل في تحميل التحليلات');
      setSummary(null);
      setCircleDistribution([]);
      setMonthlyEngagement([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  useEffect(() => {
    document.title = 'تحليل أداء الفئات - Dawayer';
  }, []);

  const contentDistributionData = circleDistribution
    .filter((d) => d.value > 0)
    .map((d) => ({
      name: d.name,
      value: d.percentage,
      color: d.color || '#D4AF37',
    }));

  const barChartData = monthlyEngagement.map((d) => ({
    month: d.period,
    engagement: d.total,
  }));

  const totalInteractions = summary?.totalInteractions ?? 0;
  const topCategory = circleDistribution[0];
  const growthPercentage = summary?.growthPercentage ?? 0;
  const topTwoCategories = circleDistribution.slice(0, 2);
  const topTwoTotal = topTwoCategories.reduce((sum, cat) => sum + (cat?.percentage || 0), 0);

  const chartLabel = timeRange === 'week' ? 'التفاعل اليومي' : timeRange === 'month' ? 'التفاعل الأسبوعي' : 'التفاعل الشهري';

  const maxEngagement = barChartData.length > 0
    ? Math.max(...barChartData.map((d) => d.engagement ?? 0), 1)
    : 1;
  const maxDigitCount = String(Math.ceil(maxEngagement)).length;
  const yAxisWidth = Math.max(50, 35 + maxDigitCount * 10);

  const CustomBarTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card p-3 rounded-lg shadow-warm border border-border">
          <p className="text-sm font-body text-foreground">
            {payload?.[0]?.payload?.month}: <span className="font-semibold">{toArabicDigits(payload?.[0]?.value)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomYAxisTick = ({ x, y, payload }) => {
    const label = toArabicDigits(payload?.value);
    const digitCount = Math.max(1, String(payload?.value ?? '').length);
    const dx = -(8 + (digitCount - 1) * 7);
    return (
      <g transform={`translate(${x},${y})`}>
        <text textAnchor="end" fill="#6B5D4F" fontFamily="Amiri" fontSize={13} dx={dx}>
          {label}
        </text>
      </g>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" dir="rtl">
      <Sidebar />
      <main className="lg:mr-60 transition-default">
        <div className="p-6 lg:p-8">
          <button
            onClick={() => navigate('/admin/settings')}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-default mb-6 group"
          >
            <Icon name="ChevronRight" size={20} color="currentColor" />
            <span className="text-sm font-body">العودة إلى الإعدادات</span>
          </button>

          <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-8">
            تحليل أداء الفئات
          </h1>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
              <p className="text-muted-foreground font-body">{error}</p>
              <button onClick={fetchAnalytics} className="mt-4 text-primary hover:underline font-body">
                إعادة المحاولة
              </button>
            </div>
          ) : (
            <>
              {/* Time range dropdown */}
              <div className="mb-6 w-fit" dir="rtl">
                <Select
                  value={timeRange}
                  onChange={setTimeRange}
                  options={TIME_RANGE_OPTIONS}
                  placeholder="اختر الفترة"
                  className="min-w-[180px]"
                />
              </div>

              {/* Performance Summary Cards - Top Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mb-10">
                <div className="text-center p-6 rounded-xl">
                  <p className="text-4xl font-heading font-bold text-[#D4AF37] mb-2">{toArabicDigits(totalInteractions)}</p>
                  <p className="text-base font-['Amiri'] text-muted-foreground">إجمالي التفاعلات</p>
                </div>
                <div className="text-center p-6 rounded-xl">
                  <p className="text-4xl font-heading font-bold text-[#D4AF37] mb-2">
                    {topCategory ? `${toArabicDigits(topCategory.percentage, { maximumFractionDigits: 1 })}%` : '—'}
                  </p>
                  <p className="text-base font-['Amiri'] text-muted-foreground">الفئة الأكثر نشاطاً</p>
                  <p className="text-sm font-['Amiri'] text-muted-foreground mt-1">
                    ({topCategory?.name ?? '—'})
                  </p>
                </div>
                <div className="text-center p-6 rounded-xl">
                  <p className="text-4xl font-heading font-bold text-[#D4AF37] mb-2">
                    {growthPercentage >= 0 ? '+' : ''}{toArabicDigits(growthPercentage, { maximumFractionDigits: 1 })}%
                  </p>
                  <p className="text-base font-['Amiri'] text-muted-foreground">النمو</p>
                </div>
              </div>

              {/* Charts Section - Middle Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl">
                {/* Pie Chart with Side Legend */}
                <div className="p-6">
                  <h2 className="text-xl font-['Amiri'] font-semibold text-foreground mb-2">
                    توزيع المحتوى
                  </h2>
                  <p className="text-sm font-['Amiri'] text-muted-foreground mb-6">
                    {topTwoCategories.length >= 2
                      ? `${topTwoCategories[0]?.name} و${topTwoCategories[1]?.name} تشكل ${toArabicDigits(topTwoTotal, { maximumFractionDigits: 1 })}% من إجمالي المحتوى.`
                      : circleDistribution.length > 0
                        ? `${toArabicDigits(summary?.totalPublishedArticles ?? 0)} مقال منشور في ${toArabicDigits(circleDistribution.length)} دائرة.`
                        : 'لا توجد مقالات منشورة بعد.'}
                  </p>
                  <div className="flex items-center gap-8">
                    <div className="flex-shrink-0 space-y-3">
                      {contentDistributionData.length > 0 ? (
                        contentDistributionData.map((entry, index) => (
                          <div key={index} className="flex items-center justify-between gap-4 min-w-[180px]">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: entry?.color }}
                              />
                              <span className="text-sm font-['Amiri'] text-foreground">{entry?.name}</span>
                            </div>
                            <span className="text-sm font-['Amiri'] font-semibold text-foreground">
                              {toArabicDigits(entry?.value, { maximumFractionDigits: 1 })}%
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm font-['Amiri'] text-muted-foreground">لا توجد بيانات</p>
                      )}
                    </div>
                    <div className="flex-1">
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={contentDistributionData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={false}
                            outerRadius={110}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {contentDistributionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry?.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Bar Chart */}
                <div className="p-6">
                  <h2 className="text-xl font-['Amiri'] font-semibold text-foreground mb-2">
                    {chartLabel}
                  </h2>
                  <p className="text-sm font-['Amiri'] text-muted-foreground mb-6">
                    {barChartData.length > 0
                      ? `إجمالي التفاعلات (مشاهدات، إعجابات، تعليقات).`
                      : 'لا توجد بيانات تفاعل.'}
                  </p>
                  {barChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={barChartData} margin={{ left: 8, right: 8, top: 5, bottom: 5 }}>
                        <XAxis
                          dataKey="month"
                          tick={{ fill: '#6B5D4F', fontFamily: 'Amiri', fontSize: 13 }}
                          axisLine={{ stroke: '#E5D5B7' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={<CustomYAxisTick />}
                          axisLine={{ stroke: '#E5D5B7' }}
                          tickLine={false}
                          width={yAxisWidth}
                          tickMargin={12}
                        />
                        <Tooltip content={<CustomBarTooltip />} />
                        <Bar
                          dataKey="engagement"
                          fill="#D4AF37"
                          radius={[6, 6, 0, 0]}
                          background={false}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center text-muted-foreground font-body">
                      لا توجد بيانات
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SettingsAnalytics;
